import { constrainDrawingPoint } from '../interaction/drawing_angle_snap.mjs';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { syncFromLegacy as syncToolFromLegacy } from '../interaction/tool_service.js?v=20260904f';
import {
  clearInteractionTimer,
  setInteractionFlag,
  setInteractionTimer
} from '../interaction/interaction_transient_state.js?v=20260904f';
import {
  rotateWorldPoint,
  uid
} from '../utils/common_constants.js?v=20260706d';
import {
  getPlanSvg,
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import { markProjectModelChanged } from '../project/project_revision_service.js';

const OBSTACLE_TYPES = {
  pipe: {
    label: 'Rohr',
    defaultDiameter: 100,
    defaultHeightFromFloorToBottom: 2200
  },
  duct: {
    label: 'Lüftung',
    defaultWidth: 300,
    defaultHeight: 200,
    defaultHeightFromFloorToBottom: 2200
  }
};

let obstacleRotatePress = null;

const OBSTACLE_MIN_DRAW_LENGTH = 20;
const OBSTACLE_MIN_SEGMENT_LENGTH = 5;

function requestObstaclePlanRender(reason = 'obstacle-drag') {
  AppRuntime.requestPlanRender(reason);
}

function requestObstacleAppRefresh(reason = 'obstacle-drop', modelChanged = false) {
  if (modelChanged) markProjectModelChanged(reason);
  AppRuntime.requestAppRefresh(false, reason);
}

function isObstacleTypingTarget(target) {
  const tag = target?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'select' || tag === 'textarea' || target?.isContentEditable;
}

function clearSystemTransientStateForObstacles() {
  const context = { currentView: S.project?.currentView || 'plan' };
  SystemRegistry.cancelInteractions(context);
  SystemRegistry.clearTransientStates({ ...context, resetTools: true });
}

/**
 * Stellt sicher ensure Obstacle State im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function ensureObstacleState() {
  if (!S.project || typeof S.project !== 'object') S.project = {};
  if (!Array.isArray(S.project.obstacles)) S.project.obstacles = [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!Array.isArray(S.selection.obstacleIds)) S.selection.obstacleIds = [];
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  if (!S.ui.pendingObstacleConfig || typeof S.ui.pendingObstacleConfig !== 'object') S.ui.pendingObstacleConfig = {};
  if (!S.ui.obstacleDraw || typeof S.ui.obstacleDraw !== 'object') {
    S.ui.obstacleDraw = { startPoint: null, currentPoint: null, lengthInput: null, points: [], angleHint: null };
  }
  normalizeObstacleDrawState(S.ui.obstacleDraw);
  if (!OBSTACLE_TYPES[S.ui.obstacleTool]) S.ui.obstacleTool = null;

  S.project.obstacles = S.project.obstacles
    .map(normalizeObstacle)
    .filter(Boolean);
  const existingIds = new Set(S.project.obstacles.map(obstacle => obstacle.id));
  S.selection.obstacleIds = S.selection.obstacleIds.filter(id => existingIds.has(id));
  if (S.selection.obstacleId && !obstacleById(S.selection.obstacleId)) {
    S.selection.obstacleId = null;
  }
  if (S.selection.obstacleId && !S.selection.obstacleIds.includes(S.selection.obstacleId)) {
    S.selection.obstacleIds = [S.selection.obstacleId];
  }
  if (!S.selection.obstacleId && S.selection.obstacleIds.length) {
    S.selection.obstacleId = S.selection.obstacleIds[0];
  }
  return S.project.obstacles;
}

/**
 * Normalisiert normalize Obstacle Point im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} point - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeObstaclePoint(point) {
  const source = point && typeof point === 'object' ? point : {};
  return {
    x: Number.isFinite(Number(source.x)) ? Number(source.x) : 0,
    z: Number.isFinite(Number(source.z)) ? Number(source.z) : Number.isFinite(Number(source.y)) ? Number(source.y) : 0
  };
}

function normalizeObstacleDrawPoint(point) {
  if (!point || typeof point !== 'object') return null;
  const normalized = normalizeObstaclePoint(point);
  return Number.isFinite(normalized.x) && Number.isFinite(normalized.z) ? normalized : null;
}

function obstaclePointsEqual(a, b, epsilon = 0.001) {
  return Boolean(a) && Boolean(b) && Math.hypot(a.x - b.x, a.z - b.z) <= epsilon;
}

function normalizeObstacleDrawState(drawState) {
  if (!drawState || typeof drawState !== 'object') return null;
  const startPoint = normalizeObstacleDrawPoint(drawState.startPoint);
  const currentPoint = normalizeObstacleDrawPoint(drawState.currentPoint);
  drawState.startPoint = startPoint ? { ...startPoint } : null;
  drawState.currentPoint = currentPoint ? { ...currentPoint } : null;
  drawState.points = Array.isArray(drawState.points)
    ? drawState.points.map(normalizeObstacleDrawPoint).filter(Boolean)
    : [];
  if (!drawState.points.length && drawState.startPoint) {
    drawState.points.push({ ...drawState.startPoint });
  }
  if (
    drawState.startPoint
    && drawState.points.length
    && !obstaclePointsEqual(drawState.points[drawState.points.length - 1], drawState.startPoint)
  ) {
    drawState.points.push({ ...drawState.startPoint });
  }
  if (!drawState.angleHint || typeof drawState.angleHint !== 'object') {
    drawState.angleHint = null;
  }
  return drawState;
}

/**
 * Normalisiert normalize Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für normalize Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeObstacle(obstacle) {
  if (!obstacle || !OBSTACLE_TYPES[obstacle.type]) return null;
  const type = obstacle.type;
  const sourceStart = obstacle.start || {
    x: Number.isFinite(Number(obstacle.x)) ? Number(obstacle.x) : 0,
    z: Number.isFinite(Number(obstacle.y)) ? Number(obstacle.y) : Number.isFinite(Number(obstacle.z)) ? Number(obstacle.z) : 0
  };
  const sourceEnd = obstacle.end;
  const normalized = {
    id: obstacle.id || uid('obs'),
    type,
    start: normalizeObstaclePoint(sourceStart),
    end: normalizeObstaclePoint(sourceEnd),
    heightFromFloorToBottom: Math.max(0, Number.isFinite(Number(obstacle.heightFromFloorToBottom))
      ? Number(obstacle.heightFromFloorToBottom)
      : OBSTACLE_TYPES[type].defaultHeightFromFloorToBottom)
  };

  if (type === 'pipe') {
    normalized.diameter = Math.max(1, Number.isFinite(Number(obstacle.diameter))
      ? Number(obstacle.diameter)
      : OBSTACLE_TYPES.pipe.defaultDiameter);
  } else if (type === 'duct') {
    normalized.width = Math.max(1, Number.isFinite(Number(obstacle.width))
      ? Number(obstacle.width)
      : OBSTACLE_TYPES.duct.defaultWidth);
    normalized.height = Math.max(1, Number.isFinite(Number(obstacle.height))
      ? Number(obstacle.height)
      : OBSTACLE_TYPES.duct.defaultHeight);
  }
  return normalized;
}

function defaultObstacleConfig(type) {
  if (type === 'pipe') {
    return {
      diameter: OBSTACLE_TYPES.pipe.defaultDiameter,
      heightFromFloorToBottom: OBSTACLE_TYPES.pipe.defaultHeightFromFloorToBottom
    };
  }
  if (type === 'duct') {
    return {
      width: OBSTACLE_TYPES.duct.defaultWidth,
      height: OBSTACLE_TYPES.duct.defaultHeight,
      heightFromFloorToBottom: OBSTACLE_TYPES.duct.defaultHeightFromFloorToBottom
    };
  }
  return {};
}

function pendingObstacleConfig(type) {
  ensureObstacleState();
  if (!OBSTACLE_TYPES[type]) return {};
  const current = S.ui.pendingObstacleConfig[type];
  const normalized = normalizeObstacle({
    id: 'pending',
    type,
    start: { x: 0, z: 0 },
    end: { x: OBSTACLE_MIN_DRAW_LENGTH, z: 0 },
    ...defaultObstacleConfig(type),
    ...(current && typeof current === 'object' ? current : {})
  });
  S.ui.pendingObstacleConfig[type] = obstacleConfigFromObstacle(normalized);
  return { ...S.ui.pendingObstacleConfig[type] };
}

function setPendingObstacleConfig(type, patch = {}) {
  ensureObstacleState();
  if (!OBSTACLE_TYPES[type] || !patch || typeof patch !== 'object') return {};
  const next = {
    ...pendingObstacleConfig(type),
    ...patch
  };
  const normalized = normalizeObstacle({
    id: 'pending',
    type,
    start: { x: 0, z: 0 },
    end: { x: Math.max(OBSTACLE_MIN_DRAW_LENGTH, Number(next.length) || OBSTACLE_MIN_DRAW_LENGTH), z: 0 },
    ...next
  });
  S.ui.pendingObstacleConfig[type] = obstacleConfigFromObstacle(normalized);
  return { ...S.ui.pendingObstacleConfig[type] };
}

function obstacleConfigFromObstacle(obstacle) {
  const normalized = normalizeObstacle(obstacle);
  if (!normalized) return {};
  if (normalized.type === 'pipe') {
    return {
      diameter: normalized.diameter,
      heightFromFloorToBottom: normalized.heightFromFloorToBottom
    };
  }
  if (normalized.type === 'duct') {
    return {
      width: normalized.width,
      height: normalized.height,
      heightFromFloorToBottom: normalized.heightFromFloorToBottom
    };
  }
  return {};
}

/**
 * Beschreibt obstacle Active Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleActiveTool() {
  ensureObstacleState();
  return S.ui.obstacleTool || null;
}

function isGlobalObstacleDrawToolActive() {
  return Boolean(obstacleActiveTool());
}

function resetObstacleDrawState() {
  ensureObstacleState();
  S.ui.obstacleDraw = { startPoint: null, currentPoint: null, lengthInput: null, points: [], angleHint: null };
  if (S.drag?.type === 'obstacleDraft') {
    S.drag.active = false;
    S.drag.type = null;
  }
}

/**
 * Aktualisiert set Obstacle Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} type - Eingabewert für set Obstacle Tool.
 * Seiteneffekte: Kann Projektstate, Auswahl, Snap-Verbindungen oder abhängige Layoutdaten verändern.
 */
function setObstacleTool(type) {
  ensureObstacleState();
  const previousTool = S.ui.obstacleTool || null;
  const nextTool = OBSTACLE_TYPES[type] ? type : null;
  S.ui.obstacleTool = nextTool;
  if (previousTool !== nextTool) resetObstacleDrawState();
  syncToolFromLegacy();
  syncObstacleToolbarButtons();
}

/**
 * Beschreibt activate Obstacle Tool im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} type - Eingabewert für activate Obstacle Tool.
 * Seiteneffekte: Beachtet Toolbar-, Werkzeug- und Bedienzustände; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
function activateObstacleTool(type) {
  ensureObstacleState();
  const nextType = S.ui.obstacleTool === type ? null : type;
  if (nextType) pendingObstacleConfig(nextType);
  if (S.ui) {
    S.ui.showAppOptions = false;
    S.ui.showAppAdminPanel = false;
  }
  clearAllObstacleBlockingSelections();
  setObstacleTool(nextType);
  if (nextType) {
    setStatus(`${OBSTACLE_TYPES[nextType].label} zeichnen · ersten Punkt setzen`);
  } else {
    setStatus('Hindernis-Werkzeug deaktiviert');
  }
  AppRuntime.refresh(false);
}

/**
 * Beschreibt sync Obstacle Toolbar Buttons im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Beachtet Toolbar-, Werkzeug- und Bedienzustände; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
function syncObstacleToolbarButtons() {
  const tool = S.ui?.obstacleTool || null;
  const pipeButton = document.getElementById('btnObstaclePipe');
  const ductButton = document.getElementById('btnObstacleDuct');
  pipeButton?.classList.toggle('is-active', tool === 'pipe');
  ductButton?.classList.toggle('is-active', tool === 'duct');
  pipeButton?.setAttribute('aria-pressed', tool === 'pipe' ? 'true' : 'false');
  ductButton?.setAttribute('aria-pressed', tool === 'duct' ? 'true' : 'false');
}

/**
 * Entfernt clear All Obstacle Blocking Selections im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Aktualisiert die globale Auswahl und synchronisiert nachgelagerte UI-Zustände.
 */
function clearAllObstacleBlockingSelections(options = {}) {
  SystemRegistry.clearAllSelections();
  AppRuntime.clearMultiSelection();
  if (options.clearTransient !== false) clearSystemTransientStateForObstacles();
  S.selection.obstacleId = null;
  S.selection.obstacleIds = [];
}

/**
 * Entfernt clear Obstacle Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Aktualisiert die globale Auswahl und synchronisiert nachgelagerte UI-Zustände.
 */
function clearObstacleSelection() {
  if (S.selection) {
    S.selection.obstacleId = null;
    S.selection.obstacleIds = [];
  }
  SelectionService.syncFromLegacy();
}

/**
 * Beschreibt obstacle By Id im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleById(id) {
  if (!id || !Array.isArray(S.project?.obstacles)) return null;
  return S.project.obstacles.find(obstacle => obstacle.id === id) || null;
}

/**
 * Setzt selected Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function selectedObstacle() {
  ensureObstacleState();
  return obstacleById(S.selection?.obstacleId);
}

function selectedObstacles() {
  ensureObstacleState();
  const ids = Array.isArray(S.selection?.obstacleIds)
    ? S.selection.obstacleIds
    : S.selection?.obstacleId
      ? [S.selection.obstacleId]
      : [];
  return ids.map(id => obstacleById(id)).filter(Boolean);
}

/**
 * Setzt select Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function selectObstacle(id, options = {}) {
  ensureObstacleState();
  const obstacle = obstacleById(id);
  if (!obstacle) return false;
  const append = Boolean(options.append) || Boolean(options.toggle);
  const previousIds = Array.isArray(S.selection.obstacleIds) ? [...S.selection.obstacleIds] : [];
  let nextIds = append ? previousIds : [];
  if (options.toggle && nextIds.includes(obstacle.id)) {
    nextIds = nextIds.filter(entry => entry !== obstacle.id);
  } else if (!nextIds.includes(obstacle.id)) {
    nextIds.push(obstacle.id);
  }
  clearSystemTransientStateForObstacles();
  setObstacleTool(null);
  if (!append || !previousIds.length) {
    clearAllObstacleBlockingSelections({ clearTransient: false });
    if (S.ui) {
      S.ui.showAppOptions = false;
      S.ui.showAppAdminPanel = false;
    }
  }
  S.selection.obstacleIds = nextIds;
  S.selection.obstacleId = nextIds[0] || null;
  SelectionService.syncFromLegacy();
  setStatus(nextIds.length > 1 ? `${nextIds.length} Hindernisse ausgewählt` : `${OBSTACLE_TYPES[obstacle.type].label} ausgewählt`);
  return true;
}

/**
 * Erzeugt create Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Erzeugung neuer Fachobjekte mit passenden Defaultwerten, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} type - Eingabewert für create Obstacle.
 * @param {*} start - Eingabewert für create Obstacle.
 * @param {*} end - Eingabewert für create Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function createObstacle(type, start, end, options = {}) {
  if (!OBSTACLE_TYPES[type]) return null;
  ensureObstacleState();
  const obstacle = normalizeObstacle({
    id: uid('obs'),
    type,
    start,
    end,
    ...pendingObstacleConfig(type)
  });
  S.project.obstacles.push(obstacle);
  if (options.select !== false) {
    S.selection.obstacleId = obstacle.id;
    S.selection.obstacleIds = [obstacle.id];
    SelectionService.syncFromLegacy();
  }
  return obstacle;
}

/**
 * Aktualisiert update Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Aktualisierung bestehender Daten und abhängiger Berechnungen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} patch - Eingabewert für update Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function updateObstacle(id, patch) {
  const obstacle = obstacleById(id);
  if (!obstacle || !patch || typeof patch !== 'object') return null;
  Object.assign(obstacle, patch);
  const normalized = normalizeObstacle(obstacle);
  Object.assign(obstacle, normalized);
  return obstacle;
}

/**
 * Entfernt delete Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Entfernen oder Zurücksetzen von State, DOM oder Auswahl, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function deleteObstacle(id) {
  ensureObstacleState();
  const before = S.project.obstacles.length;
  S.project.obstacles = S.project.obstacles.filter(obstacle => obstacle.id !== id);
  if (S.selection?.obstacleId === id) S.selection.obstacleId = null;
  if (Array.isArray(S.selection?.obstacleIds)) {
    S.selection.obstacleIds = S.selection.obstacleIds.filter(entry => entry !== id);
    if (!S.selection.obstacleId && S.selection.obstacleIds.length) S.selection.obstacleId = S.selection.obstacleIds[0];
  }
  SelectionService.syncFromLegacy();
  const removed = S.project.obstacles.length !== before;
  return removed;
}

function deleteSelectedObstacles() {
  ensureObstacleState();
  const ids = new Set(selectedObstacles().map(obstacle => obstacle.id));
  if (!ids.size) return false;
  const before = S.project.obstacles.length;
  S.project.obstacles = S.project.obstacles.filter(obstacle => !ids.has(obstacle.id));
  S.selection.obstacleId = null;
  S.selection.obstacleIds = [];
  SelectionService.syncFromLegacy();
  const removed = S.project.obstacles.length !== before;
  return removed;
}

/**
 * Beschreibt obstacle Height Range im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Height Range.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleHeightRange(obstacle) {
  const normalized = normalizeObstacle(obstacle);
  if (!normalized) return { bottom: 0, top: 0 };
  const bottom = normalized.heightFromFloorToBottom;
  const top = normalized.type === 'pipe'
    ? bottom + normalized.diameter
    : bottom + normalized.height;
  return { bottom, top };
}

/**
 * Beschreibt obstacle Plan Radius im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Plan Radius.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstaclePlanRadius(obstacle) {
  const normalized = normalizeObstacle(obstacle);
  if (!normalized) return 1;
  if (normalized.type === 'pipe') return Math.max(1, normalized.diameter / 2);
  return Math.max(1, normalized.width / 2);
}

function buildObstacleSegmentConnectionGraph(obstacles = [], options = {}) {
  const tolerance = Number.isFinite(Number(options.tolerance)) ? Math.max(0.5, Number(options.tolerance)) : 2;
  const allowedTypes = Array.isArray(options.types) && options.types.length
    ? new Set(options.types)
    : null;
  const models = obstacles
    .map(obstacle => obstacleSegmentGraphModel(obstacle, allowedTypes))
    .filter(Boolean);
  const joints = [];
  for (let i = 0; i < models.length; i += 1) {
    for (let j = i + 1; j < models.length; j += 1) {
      pipeGraphAddPairJoints(joints, models[i], models[j], tolerance);
    }
  }
  const mergedJoints = pipeGraphMergeJoints(joints, tolerance);
  const modelsById = new Map(models.map(model => [model.id, model]));
  mergedJoints.forEach(joint => {
    joint.miterCorner = obstacleSegmentJointCanMiter(joint, modelsById);
  });
  const byPipeId = new Map();
  for (const model of models) byPipeId.set(model.id, []);
  mergedJoints.forEach(joint => {
    (joint.connections || []).forEach(connection => {
      if (!byPipeId.has(connection.pipeId)) byPipeId.set(connection.pipeId, []);
      byPipeId.get(connection.pipeId).push(joint);
    });
  });
  return { pipes: models, joints: mergedJoints, byPipeId };
}

function buildPipeConnectionGraph(pipes = [], options = {}) {
  return buildObstacleSegmentConnectionGraph(pipes, { ...options, types: ['pipe'] });
}

function detectObstacleSegmentConnections(obstacles = [], options = {}) {
  return buildObstacleSegmentConnectionGraph(obstacles, options).joints;
}

function detectPipeConnections(pipes = [], options = {}) {
  return buildPipeConnectionGraph(pipes, options).joints;
}

function buildObstacleSegmentRenderSegments(obstacles = [], options = {}) {
  const tolerance = Number.isFinite(Number(options.tolerance)) ? Math.max(0.5, Number(options.tolerance)) : 2;
  const allowedTypes = Array.isArray(options.types) && options.types.length
    ? new Set(options.types)
    : null;
  const models = obstacles
    .map(obstacle => obstacleSegmentGraphModel(obstacle, allowedTypes))
    .filter(Boolean);
  const groups = [];

  for (const model of models) {
    let group = groups.find(candidate => pipeGraphModelFitsRenderGroup(model, candidate, tolerance));
    if (!group) {
      group = {
        base: model,
        dir: { ...model.dir },
        normalOffset: pipeGraphLineOffset(model),
        diameter: model.diameter,
        width: model.width,
        height: model.height,
        centerY: model.centerY,
        heightFromFloorToBottom: model.heightFromFloorToBottom,
        type: model.type,
        intervals: []
      };
      groups.push(group);
    }
    const a = pipeGraphDot({ x: model.start.x - group.base.start.x, z: model.start.z - group.base.start.z }, group.dir);
    const b = pipeGraphDot({ x: model.end.x - group.base.start.x, z: model.end.z - group.base.start.z }, group.dir);
    group.intervals.push({
      min: Math.min(a, b),
      max: Math.max(a, b),
      ids: [model.id]
    });
  }

  const result = [];
  for (const group of groups) {
    const sorted = group.intervals
      .filter(interval => interval.max - interval.min > 0.001)
      .sort((a, b) => a.min - b.min);
    const merged = [];
    for (const interval of sorted) {
      const last = merged[merged.length - 1];
      if (!last || interval.min > last.max + tolerance) {
        merged.push({ ...interval, ids: [...interval.ids] });
      } else {
        last.max = Math.max(last.max, interval.max);
        last.ids.push(...interval.ids);
      }
    }

    merged.forEach((interval, index) => {
      const start = {
        x: group.base.start.x + group.dir.x * interval.min,
        z: group.base.start.z + group.dir.z * interval.min
      };
      const end = {
        x: group.base.start.x + group.dir.x * interval.max,
        z: group.base.start.z + group.dir.z * interval.max
      };
      result.push({
        ...pipeGraphCloneObstacle(group.base.source),
        id: `${[...new Set(interval.ids)].join('+') || group.base.id}:render:${index}`,
        type: group.type,
        start,
        end,
        diameter: group.diameter,
        width: group.width,
        height: group.height,
        heightFromFloorToBottom: group.heightFromFloorToBottom,
        sourceObstacleIds: [...new Set(interval.ids)],
        sourcePipeIds: [...new Set(interval.ids)]
      });
    });
  }
  return result;
}

function buildPipeRenderSegments(pipes = [], options = {}) {
  return buildObstacleSegmentRenderSegments(pipes, { ...options, types: ['pipe'] });
}

function buildRectangularObstacleRenderSegments(obstacles = [], options = {}) {
  const renderSegments = buildObstacleSegmentRenderSegments(obstacles, options);
  return attachRectangularObstacleMiterPolygons(renderSegments, obstacles, options);
}

function attachRectangularObstacleMiterPolygons(renderSegments = [], sourceObstacles = [], options = {}) {
  if (!Array.isArray(renderSegments) || !renderSegments.length) return renderSegments;
  const allowedTypes = Array.isArray(options.types) && options.types.length
    ? new Set(options.types)
    : new Set(['duct']);
  if (![...allowedTypes].some(type => type === 'duct')) return renderSegments;
  const tolerance = Number.isFinite(Number(options.tolerance)) ? Math.max(0.5, Number(options.tolerance)) : 2;
  const graph = buildObstacleSegmentConnectionGraph(sourceObstacles, options);
  const sourceModels = new Map((graph.pipes || []).map(model => [model.id, model]));
  const miterLookup = rectangularObstacleMiterLookup(graph.joints || [], sourceModels);

  return renderSegments.map(segment => {
    const model = obstacleSegmentGraphModel(segment, allowedTypes);
    if (!model || model.type === 'pipe') return segment;
    const sourceIds = Array.isArray(segment.sourceObstacleIds) && segment.sourceObstacleIds.length
      ? segment.sourceObstacleIds
      : segment.id
        ? [segment.id]
        : [];
    const endpointMiters = {
      start: rectangularObstacleEndpointMiterInfo(model, 'start', sourceIds, sourceModels, miterLookup, tolerance),
      end: rectangularObstacleEndpointMiterInfo(model, 'end', sourceIds, sourceModels, miterLookup, tolerance)
    };
    if (!endpointMiters.start && !endpointMiters.end) return segment;
    const polygons = rectangularObstacleMiterPolygons(model, endpointMiters);
    return polygons
      ? { ...segment, __miterPolygons: polygons }
      : segment;
  });
}

function rectangularObstacleMiterLookup(joints = [], modelsById = new Map()) {
  const lookup = new Map();
  for (const joint of joints || []) {
    if (!joint?.miterCorner) continue;
    const connections = (joint.connections || []).filter(connection => (
      connection?.pipeId && (connection.role === 'start' || connection.role === 'end')
    ));
    if (connections.length !== 2) continue;
    const a = connections[0];
    const b = connections[1];
    const modelA = modelsById.get(a.pipeId);
    const modelB = modelsById.get(b.pipeId);
    if (!modelA || !modelB) continue;
    lookup.set(`${a.pipeId}:${a.role}`, { model: modelA, role: a.role, otherModel: modelB, otherRole: b.role });
    lookup.set(`${b.pipeId}:${b.role}`, { model: modelB, role: b.role, otherModel: modelA, otherRole: a.role });
  }
  return lookup;
}

function rectangularObstacleEndpointMiterInfo(model, role, sourceIds, sourceModels, miterLookup, tolerance) {
  const endpoint = role === 'start' ? model.start : model.end;
  for (const id of sourceIds || []) {
    const sourceModel = sourceModels.get(id);
    if (!sourceModel) continue;
    const sourceEndpoint = role === 'start' ? sourceModel.start : sourceModel.end;
    if (!obstaclePointsEqual(sourceEndpoint, endpoint, tolerance)) continue;
    const info = miterLookup.get(`${id}:${role}`);
    if (!info) continue;
    return info;
  }
  return null;
}

function rectangularObstacleMiterPolygons(model, endpointMiters = {}) {
  if (!model || model.type !== 'duct') return null;
  const half = Math.max(0.5, model.width / 2);
  return {
    body: rectangularObstacleMiterOffsetPolygon(model, { min: -half, max: half }, endpointMiters)
  };
}

function rectangularObstacleMiterOffsetPolygon(model, range, endpointMiters = {}) {
  const min = Number.isFinite(Number(range?.min)) ? Number(range.min) : 0;
  const max = Number.isFinite(Number(range?.max)) ? Number(range.max) : 0;
  const startMax = rectangularObstacleMiterOffsetPoint(model, 'start', max, 'max', range, endpointMiters.start);
  const endMax = rectangularObstacleMiterOffsetPoint(model, 'end', max, 'max', range, endpointMiters.end);
  const endMin = rectangularObstacleMiterOffsetPoint(model, 'end', min, 'min', range, endpointMiters.end);
  const startMin = rectangularObstacleMiterOffsetPoint(model, 'start', min, 'min', range, endpointMiters.start);
  return [startMax, endMax, endMin, startMin].filter(Boolean);
}

function rectangularObstacleMiterOffsetPoint(model, role, offset, side, range, miterInfo) {
  const endpoint = role === 'start' ? model.start : model.end;
  const normal = { x: -model.dir.z, z: model.dir.x };
  const fallback = {
    x: endpoint.x + normal.x * offset,
    z: endpoint.z + normal.z * offset
  };
  if (!miterInfo?.otherModel) return fallback;
  const other = miterInfo.otherModel;
  const otherEndpoint = miterInfo.otherRole === 'start' ? other.start : other.end;
  const otherNormal = { x: -other.dir.z, z: other.dir.x };
  const useOppositeSide = role === miterInfo.otherRole;
  const otherOffset = useOppositeSide
    ? (side === 'max' ? range.min : range.max)
    : offset;
  const otherPoint = {
    x: otherEndpoint.x + otherNormal.x * otherOffset,
    z: otherEndpoint.z + otherNormal.z * otherOffset
  };
  const intersection = rectangularObstacleLineIntersection(fallback, model.dir, otherPoint, other.dir);
  if (!intersection) return fallback;
  const width = Math.max(1, Math.abs((Number.isFinite(Number(range?.max)) ? Number(range.max) : 0) - (Number.isFinite(Number(range?.min)) ? Number(range.min) : 0)));
  const maxMiter = Math.max(12000, width * 40);
  return Math.hypot(intersection.x - endpoint.x, intersection.z - endpoint.z) <= maxMiter
    ? intersection
    : fallback;
}

function rectangularObstacleLineIntersection(pointA, dirA, pointB, dirB) {
  const denominator = pipeGraphCross(dirA, dirB);
  if (Math.abs(denominator) <= 0.0001) return null;
  const delta = { x: pointB.x - pointA.x, z: pointB.z - pointA.z };
  const t = pipeGraphCross(delta, dirB) / denominator;
  return {
    x: pointA.x + dirA.x * t,
    z: pointA.z + dirA.z * t
  };
}

function obstacleSegmentJointCanMiter(joint, modelsById = new Map()) {
  const connections = (joint?.connections || []).filter(connection => (
    connection?.pipeId && (connection.role === 'start' || connection.role === 'end')
  ));
  if (connections.length !== 2 || (joint.connections || []).length !== 2) return false;
  const a = modelsById.get(connections[0].pipeId);
  const b = modelsById.get(connections[1].pipeId);
  if (!a || !b || a.type === 'pipe' || b.type === 'pipe') return false;
  if (!pipeGraphDimensionsCompatible(a, b) || a.renderKey !== b.renderKey) return false;
  return Math.abs(pipeGraphCross(a.dir, b.dir)) > 0.01;
}

function createObstacleSegmentMoveTopology(selectedIds = []) {
  ensureObstacleState();
  const selectedSegmentIds = [...new Set(selectedIds)]
    .map(String)
    .filter(id => {
      const obstacle = obstacleById(id);
      return obstacle?.type === 'pipe' || obstacle?.type === 'duct';
    });
  if (!selectedSegmentIds.length) return null;
  const segments = S.project.obstacles.filter(obstacle => (
    obstacle.type === 'pipe' || obstacle.type === 'duct'
  ));
  const graph = buildObstacleSegmentConnectionGraph(segments);
  return {
    selectedSegmentIds,
    selectedPipeIds: selectedSegmentIds.filter(id => obstacleById(id)?.type === 'pipe'),
    pipeIds: segments.map(segment => segment.id),
    startPipes: new Map(segments.map(segment => [segment.id, pipeGraphCloneObstacle(segment)])),
    graph
  };
}

function createPipeMoveTopology(selectedIds = []) {
  ensureObstacleState();
  const selectedPipeIds = [...new Set(selectedIds)]
    .map(String)
    .filter(id => obstacleById(id)?.type === 'pipe');
  if (!selectedPipeIds.length) return null;
  const pipes = S.project.obstacles.filter(obstacle => obstacle.type === 'pipe');
  const graph = buildPipeConnectionGraph(pipes);
  return {
    selectedPipeIds,
    pipeIds: pipes.map(pipe => pipe.id),
    startPipes: new Map(pipes.map(pipe => [pipe.id, pipeGraphCloneObstacle(pipe)])),
    graph
  };
}

function updateConnectedObstacleSegmentMove(topology, dx, dz) {
  const selectedSegmentIds = topology?.selectedSegmentIds || topology?.selectedPipeIds || [];
  if (!selectedSegmentIds.length || !topology?.startPipes) return false;
  const selectedIds = new Set(selectedSegmentIds);
  const changedIds = new Set();

  for (const id of topology.pipeIds || []) {
    const pipe = obstacleById(id);
    const snapshot = topology.startPipes.get(id);
    if (!pipe || !snapshot) continue;
    pipe.start = { ...snapshot.start };
    pipe.end = { ...snapshot.end };
  }

  selectedIds.forEach(id => {
    const pipe = obstacleById(id);
    const snapshot = topology.startPipes.get(id);
    if (!pipe || !snapshot) return;
    pipe.start = { x: snapshot.start.x + dx, z: snapshot.start.z + dz };
    pipe.end = { x: snapshot.end.x + dx, z: snapshot.end.z + dz };
    changedIds.add(id);
  });

  const selectedMovedModels = new Map();
  selectedIds.forEach(id => {
    const segment = obstacleById(id);
    const model = obstacleSegmentGraphModel(segment);
    if (model) selectedMovedModels.set(id, model);
  });

  for (const joint of topology.graph?.joints || []) {
    const connections = Array.isArray(joint.connections) ? joint.connections : [];
    const selectedConnections = connections.filter(connection => selectedIds.has(connection.pipeId));
    const otherConnections = connections.filter(connection => !selectedIds.has(connection.pipeId));
    if (!selectedConnections.length || !otherConnections.length) continue;
    const selectedAnchor = selectedConnections.find(connection => selectedMovedModels.has(connection.pipeId));
    const otherAnchor = otherConnections
      .map(connection => ({ connection, model: obstacleSegmentGraphModel(topology.startPipes.get(connection.pipeId)) }))
      .find(entry => entry.model);
    if (!selectedAnchor || !otherAnchor) continue;

    const selectedModel = selectedMovedModels.get(selectedAnchor.pipeId);
    const jointPoint = pipeGraphConstrainedJointPoint(
      selectedModel,
      otherAnchor.model,
      pipeGraphConnectionPoint(selectedModel, selectedAnchor)
    );

    selectedConnections.forEach(connection => {
      const pipe = obstacleById(connection.pipeId);
      if (pipeGraphApplyConnectionPoint(pipe, connection, jointPoint)) {
        changedIds.add(connection.pipeId);
        const updated = obstacleSegmentGraphModel(pipe);
        if (updated) selectedMovedModels.set(connection.pipeId, updated);
      }
    });

    otherConnections.forEach(connection => {
      const pipe = obstacleById(connection.pipeId);
      if (pipeGraphApplyConnectionPoint(pipe, connection, jointPoint)) {
        changedIds.add(connection.pipeId);
      }
    });
  }

  changedIds.forEach(id => {
    const pipe = obstacleById(id);
    if (!pipe) return;
    const normalized = normalizeObstacle(pipe);
    if (normalized) Object.assign(pipe, normalized);
  });
  return changedIds.size > 0;
}

function updateConnectedPipeMove(topology, dx, dz) {
  return updateConnectedObstacleSegmentMove(topology, dx, dz);
}

function pipeGraphModel(pipe) {
  return obstacleSegmentGraphModel(pipe, new Set(['pipe']));
}

function obstacleSegmentGraphModel(obstacle, allowedTypes = null) {
  const normalized = normalizeObstacle(obstacle);
  if (
    !normalized
    || !normalized.start
    || !normalized.end
    || (allowedTypes && !allowedTypes.has(normalized.type))
    || !['pipe', 'duct'].includes(normalized.type)
  ) return null;
  const dx = normalized.end.x - normalized.start.x;
  const dz = normalized.end.z - normalized.start.z;
  const length = Math.hypot(dx, dz);
  if (length <= 0.001) return null;
  const radius = obstacleSegmentGraphRadius(normalized);
  const range = obstacleHeightRange(normalized);
  return {
    id: normalized.id || '',
    type: normalized.type,
    source: obstacle,
    start: { ...normalized.start },
    end: { ...normalized.end },
    dx,
    dz,
    length,
    dir: { x: dx / length, z: dz / length },
    diameter: normalized.diameter,
    width: normalized.width,
    height: normalized.height,
    thickness: normalized.thickness,
    radius,
    heightFromFloorToBottom: normalized.heightFromFloorToBottom,
    centerY: (range.bottom + range.top) / 2,
    heightRange: range,
    renderKey: obstacleSegmentGraphRenderKey(normalized)
  };
}

function obstacleSegmentGraphRadius(obstacle) {
  if (obstacle.type === 'pipe') return Math.max(0.5, obstacle.diameter / 2);
  return Math.max(0.5, obstacle.width / 2);
}

function obstacleSegmentGraphRenderKey(obstacle) {
  if (obstacle.type === 'pipe') {
    return `pipe:${Math.round(obstacle.diameter * 100) / 100}:${Math.round(obstacle.heightFromFloorToBottom * 100) / 100}`;
  }
  return [
    'duct',
    Math.round(obstacle.width * 100) / 100,
    Math.round(obstacle.height * 100) / 100,
    Math.round(obstacle.heightFromFloorToBottom * 100) / 100
  ].join(':');
}

function pipeGraphAddPairJoints(joints, a, b, tolerance) {
  if (!pipeGraphDimensionsCompatible(a, b)) return;
  const intersection = pipeGraphSegmentIntersection(a, b, tolerance);
  if (intersection) pipeGraphPushJoint(joints, intersection.point, [
    pipeGraphConnectionAtT(a, intersection.t),
    pipeGraphConnectionAtT(b, intersection.u)
  ], [a, b]);

  for (const endpoint of pipeGraphEndpointEntries(a)) {
    pipeGraphPushEndpointProjectionJoint(joints, endpoint, b, tolerance);
  }
  for (const endpoint of pipeGraphEndpointEntries(b)) {
    pipeGraphPushEndpointProjectionJoint(joints, endpoint, a, tolerance);
  }

  const overlap = pipeGraphCollinearOverlap(a, b, tolerance);
  if (overlap) {
    pipeGraphPushJoint(joints, overlap.start, [
      pipeGraphConnectionAtPoint(a, overlap.start),
      pipeGraphConnectionAtPoint(b, overlap.start)
    ], [a, b]);
    pipeGraphPushJoint(joints, overlap.end, [
      pipeGraphConnectionAtPoint(a, overlap.end),
      pipeGraphConnectionAtPoint(b, overlap.end)
    ], [a, b]);
  }
}

function pipeGraphPushEndpointProjectionJoint(joints, endpoint, other, tolerance) {
  const projection = pipeGraphProjectPointToSegment(endpoint.point, other);
  const connectionDistance = Math.max(tolerance, Math.min(endpoint.model.radius, other.radius));
  const slack = connectionDistance / Math.max(1, other.length);
  if (projection.t < -slack || projection.t > 1 + slack || projection.distance > connectionDistance) return;
  pipeGraphPushJoint(joints, { x: projection.x, z: projection.z }, [
    { pipeId: endpoint.model.id, role: endpoint.role, t: endpoint.role === 'start' ? 0 : 1 },
    pipeGraphConnectionAtT(other, projection.t)
  ], [endpoint.model, other]);
}

function pipeGraphSegmentIntersection(a, b, tolerance) {
  const denominator = pipeGraphCross({ x: a.dx, z: a.dz }, { x: b.dx, z: b.dz });
  if (Math.abs(denominator) <= 0.0001) return null;
  const qp = { x: b.start.x - a.start.x, z: b.start.z - a.start.z };
  const t = pipeGraphCross(qp, { x: b.dx, z: b.dz }) / denominator;
  const u = pipeGraphCross(qp, { x: a.dx, z: a.dz }) / denominator;
  const slackA = tolerance / Math.max(1, a.length);
  const slackB = tolerance / Math.max(1, b.length);
  if (t < -slackA || t > 1 + slackA || u < -slackB || u > 1 + slackB) return null;
  const clampedT = Math.min(1, Math.max(0, t));
  return {
    point: {
      x: a.start.x + a.dx * clampedT,
      z: a.start.z + a.dz * clampedT
    },
    t,
    u
  };
}

function pipeGraphLineIntersection(a, b) {
  const denominator = pipeGraphCross(a.dir, b.dir);
  if (Math.abs(denominator) <= 0.0001) return null;
  const qp = { x: b.start.x - a.start.x, z: b.start.z - a.start.z };
  const t = pipeGraphCross(qp, b.dir) / denominator;
  return {
    x: a.start.x + a.dir.x * t,
    z: a.start.z + a.dir.z * t
  };
}

function pipeGraphConstrainedJointPoint(selectedModel, fixedModel, fallbackPoint) {
  const intersection = pipeGraphLineIntersection(selectedModel, fixedModel);
  if (intersection) return intersection;
  const projection = pipeGraphProjectPointToLine(fallbackPoint, fixedModel);
  return projection || fallbackPoint;
}

function pipeGraphCollinearOverlap(a, b, tolerance) {
  if (Math.abs(pipeGraphCross(a.dir, b.dir)) > 0.01) return null;
  if (pipeGraphPointLineDistance(b.start, a) > tolerance) return null;
  const b0 = pipeGraphDot({ x: b.start.x - a.start.x, z: b.start.z - a.start.z }, a.dir);
  const b1 = pipeGraphDot({ x: b.end.x - a.start.x, z: b.end.z - a.start.z }, a.dir);
  const min = Math.max(0, Math.min(b0, b1));
  const max = Math.min(a.length, Math.max(b0, b1));
  if (max - min <= tolerance) return null;
  return {
    start: { x: a.start.x + a.dir.x * min, z: a.start.z + a.dir.z * min },
    end: { x: a.start.x + a.dir.x * max, z: a.start.z + a.dir.z * max }
  };
}

function pipeGraphPushJoint(joints, point, connections, models) {
  if (!point || !connections?.length) return;
  const uniqueConnections = [];
  for (const connection of connections) {
    if (!connection?.pipeId) continue;
    if (uniqueConnections.some(entry => entry.pipeId === connection.pipeId && entry.role === connection.role)) continue;
    uniqueConnections.push(connection);
  }
  if (uniqueConnections.length < 2) return;
  joints.push({
    x: point.x,
    z: point.z,
    radius: Math.max(...models.map(model => model.radius)),
    centerY: models.reduce((sum, model) => sum + model.centerY, 0) / models.length,
    pipeIds: [...new Set(uniqueConnections.map(connection => connection.pipeId))],
    connections: uniqueConnections
  });
}

function pipeGraphMergeJoints(joints, tolerance) {
  const merged = [];
  joints.forEach(joint => {
    const existing = merged.find(candidate => Math.hypot(candidate.x - joint.x, candidate.z - joint.z) <= tolerance);
    if (!existing) {
      merged.push({
        ...joint,
        pipeIds: [...new Set(joint.pipeIds || [])],
        connections: [...(joint.connections || [])]
      });
      return;
    }
    existing.x = (existing.x + joint.x) / 2;
    existing.z = (existing.z + joint.z) / 2;
    existing.radius = Math.max(existing.radius, joint.radius);
    existing.centerY = Math.max(existing.centerY || 0, joint.centerY || 0);
    existing.pipeIds = [...new Set([...(existing.pipeIds || []), ...(joint.pipeIds || [])])];
    for (const connection of joint.connections || []) {
      if (!existing.connections.some(entry => entry.pipeId === connection.pipeId && entry.role === connection.role)) {
        existing.connections.push(connection);
      }
    }
  });
  return merged;
}

function pipeGraphEndpointEntries(model) {
  return [
    { model, role: 'start', point: model.start },
    { model, role: 'end', point: model.end }
  ];
}

function pipeGraphConnectionAtT(model, t) {
  const slack = Math.max(0.001, 2 / Math.max(1, model.length));
  const clamped = Math.min(1, Math.max(0, Number.isFinite(Number(t)) ? Number(t) : 0));
  if (clamped <= slack) return { pipeId: model.id, role: 'start', t: 0 };
  if (clamped >= 1 - slack) return { pipeId: model.id, role: 'end', t: 1 };
  return { pipeId: model.id, role: 'side', t: clamped };
}

function pipeGraphConnectionAtPoint(model, point) {
  const projection = pipeGraphProjectPointToSegment(point, model);
  return pipeGraphConnectionAtT(model, projection.t);
}

function pipeGraphConnectionPoint(model, connection) {
  if (connection?.role === 'start') return { ...model.start };
  if (connection?.role === 'end') return { ...model.end };
  const t = Math.min(1, Math.max(0, Number.isFinite(Number(connection?.t)) ? Number(connection.t) : 0));
  return {
    x: model.start.x + model.dx * t,
    z: model.start.z + model.dz * t
  };
}

function pipeGraphApplyConnectionPoint(pipe, connection, point) {
  if (!pipe || !connection || !point) return false;
  if (connection.role === 'start') {
    pipe.start = { x: point.x, z: point.z };
    return true;
  }
  if (connection.role === 'end') {
    pipe.end = { x: point.x, z: point.z };
    return true;
  }
  return false;
}

function pipeGraphProjectPointToSegment(point, segment) {
  const vx = point.x - segment.start.x;
  const vz = point.z - segment.start.z;
  const lengthSquared = segment.length * segment.length;
  const t = lengthSquared > 0 ? (vx * segment.dx + vz * segment.dz) / lengthSquared : 0;
  const clamped = Math.min(1, Math.max(0, t));
  const x = segment.start.x + segment.dx * clamped;
  const z = segment.start.z + segment.dz * clamped;
  return { x, z, t, distance: Math.hypot(point.x - x, point.z - z) };
}

function pipeGraphProjectPointToLine(point, segment) {
  if (!point || !segment) return null;
  const t = pipeGraphDot({ x: point.x - segment.start.x, z: point.z - segment.start.z }, segment.dir);
  return {
    x: segment.start.x + segment.dir.x * t,
    z: segment.start.z + segment.dir.z * t
  };
}

function pipeGraphPointLineDistance(point, segment) {
  return Math.abs(pipeGraphCross(
    { x: point.x - segment.start.x, z: point.z - segment.start.z },
    { x: segment.dx, z: segment.dz }
  )) / Math.max(1, segment.length);
}

function pipeGraphDimensionsCompatible(a, b) {
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === 'pipe') {
    return Math.abs(a.diameter - b.diameter) <= 0.5
      && Math.abs(a.centerY - b.centerY) <= 0.5;
  }
  if (a.type === 'duct') {
    return Math.abs(a.width - b.width) <= 0.5
      && Math.abs(a.height - b.height) <= 0.5
      && Math.abs(a.heightFromFloorToBottom - b.heightFromFloorToBottom) <= 0.5;
  }
  return Math.abs(a.radius - b.radius) <= 0.5
    && Math.abs(a.height - b.height) <= 0.5
    && Math.abs(a.heightRange.bottom - b.heightRange.bottom) <= 0.5;
}

function pipeGraphModelFitsRenderGroup(model, group, tolerance) {
  return pipeGraphDimensionsCompatible(model, group.base)
    && model.renderKey === group.base.renderKey
    && Math.abs(pipeGraphCross(model.dir, group.dir)) <= 0.01
    && Math.abs(pipeGraphCross(model.start, group.dir) - group.normalOffset) <= tolerance;
}

function pipeGraphLineOffset(model) {
  return pipeGraphCross(model.start, model.dir);
}

function pipeGraphCloneObstacle(obstacle) {
  return {
    ...obstacle,
    start: { ...obstacle.start },
    end: { ...obstacle.end }
  };
}

function pipeGraphCross(a, b) {
  return a.x * b.z - a.z * b.x;
}

function pipeGraphDot(a, b) {
  return a.x * b.x + a.z * b.z;
}

/**
 * Beschreibt obstacle World From Mouse Event im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleWorldFromMouseEvent(event) {
  const svg = getPlanSvg();
  const rect = svg.getBoundingClientRect();
  return screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
}

function obstacleConstrainPoint(start, point, event) {
  return constrainDrawingPoint(start, point, event?.shiftKey);
}

function obstacleDrawPointFromEvent(event) {
  const point = obstacleWorldFromMouseEvent(event);
  const start = S.ui?.obstacleDraw?.startPoint || null;
  return obstacleConstrainPoint(start, point, event);
}

function obstacleDrawAngle(start, end) {
  if (!start || !end) return 0;
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  if (Math.hypot(dx, dz) <= 0.001) return 0;
  return Math.atan2(dz, dx);
}

function obstaclePointAtLength(start, angle, length) {
  return {
    x: start.x + Math.cos(angle) * length,
    z: start.z + Math.sin(angle) * length
  };
}

function calculateSegmentAngle(previousStart, jointPoint, currentEnd) {
  if (!previousStart || !jointPoint || !currentEnd) return null;
  const previousVector = {
    x: jointPoint.x - previousStart.x,
    z: jointPoint.z - previousStart.z
  };
  const currentVector = {
    x: currentEnd.x - jointPoint.x,
    z: currentEnd.z - jointPoint.z
  };
  const previousLength = Math.hypot(previousVector.x, previousVector.z);
  const currentLength = Math.hypot(currentVector.x, currentVector.z);
  if (previousLength <= 0.001 || currentLength <= 0.001) return null;
  const cosine = (
    previousVector.x * currentVector.x
    + previousVector.z * currentVector.z
  ) / (previousLength * currentLength);
  const clamped = Math.max(-1, Math.min(1, cosine));
  return Math.acos(clamped) * 180 / Math.PI;
}

function formatSegmentAngle(angle) {
  return `${Math.round(angle)}\u00b0`;
}

function clearAngleHint() {
  if (S.ui?.obstacleDraw) S.ui.obstacleDraw.angleHint = null;
}

function obstacleDrawFixedPoints(drawState) {
  if (!drawState || typeof drawState !== 'object') return [];
  normalizeObstacleDrawState(drawState);
  return Array.isArray(drawState.points) ? drawState.points : [];
}

function setObstacleDrawFixedStart(point) {
  const drawState = S.ui?.obstacleDraw;
  const normalized = normalizeObstacleDrawPoint(point);
  if (!drawState || !normalized) return;
  drawState.points = [{ ...normalized }];
  clearAngleHint();
}

function appendObstacleDrawFixedPoint(point) {
  const drawState = S.ui?.obstacleDraw;
  const normalized = normalizeObstacleDrawPoint(point);
  if (!drawState || !normalized) return;
  const points = obstacleDrawFixedPoints(drawState);
  const lastPoint = points[points.length - 1] || null;
  if (!lastPoint || !obstaclePointsEqual(lastPoint, normalized)) {
    points.push({ ...normalized });
  }
  drawState.points = points;
  clearAngleHint();
}

function updateObstacleDrawAngleHint(currentPoint) {
  const drawState = S.ui?.obstacleDraw;
  const normalizedCurrent = normalizeObstacleDrawPoint(currentPoint);
  if (!drawState || !normalizedCurrent) return null;
  const points = obstacleDrawFixedPoints(drawState);
  if (points.length < 2) {
    clearAngleHint();
    return null;
  }
  const jointPoint = points[points.length - 1];
  const previousStart = points[points.length - 2];
  const angle = calculateSegmentAngle(previousStart, jointPoint, normalizedCurrent);
  if (!Number.isFinite(angle)) {
    clearAngleHint();
    return null;
  }
  drawState.angleHint = {
    angle,
    label: formatSegmentAngle(angle),
    position: { ...normalizedCurrent },
    jointPoint: { ...jointPoint }
  };
  return drawState.angleHint;
}

function parseObstacleLengthInput(value) {
  const text = String(value || '').trim().replace(',', '.');
  if (!text) return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function syncObstacleLengthInputPreview() {
  const drawState = S.ui?.obstacleDraw;
  const input = drawState?.lengthInput;
  if (!drawState?.startPoint || !input?.active) return;
  const parsed = parseObstacleLengthInput(input.value);
  if (Number.isFinite(parsed) && parsed > 0) {
    drawState.currentPoint = obstaclePointAtLength(drawState.startPoint, input.angle, parsed);
    updateObstacleDrawAngleHint(drawState.currentPoint);
  } else {
    clearAngleHint();
  }
}

function isObstacleLengthInputCharacter(key) {
  return /^[0-9]$/.test(key) || key === '.' || key === ',';
}

function startObstacleLengthInput(key) {
  const type = obstacleActiveTool();
  const drawState = S.ui?.obstacleDraw;
  if (!type || !drawState?.startPoint) return false;
  const angle = obstacleDrawAngle(drawState.startPoint, drawState.currentPoint || drawState.startPoint);
  drawState.lengthInput = {
    active: true,
    value: key,
    startPoint: { ...drawState.startPoint },
    angle,
    toolType: type
  };
  syncObstacleLengthInputPreview();
  setStatus(`${OBSTACLE_TYPES[type].label} Länge eingeben`);
  AppRuntime.refresh(false);
  return true;
}

function appendObstacleLengthInput(key) {
  const input = S.ui?.obstacleDraw?.lengthInput;
  if (!input?.active) return false;
  if ((key === '.' || key === ',') && /[.,]/.test(input.value)) return true;
  input.value += key;
  syncObstacleLengthInputPreview();
  AppRuntime.refresh(false);
  return true;
}

function cancelObstacleLengthInput() {
  const drawState = S.ui?.obstacleDraw;
  if (!drawState?.lengthInput?.active) return false;
  drawState.lengthInput = null;
  updateObstacleDrawAngleHint(drawState.currentPoint);
  setStatus('Längeneingabe abgebrochen');
  AppRuntime.refresh(false);
  return true;
}

function commitObstacleSegment(type, start, end, statusSuffix = 'nächsten Punkt setzen') {
  const length = Math.hypot(end.x - start.x, end.z - start.z);
  if (length < OBSTACLE_MIN_SEGMENT_LENGTH) {
    if (S.ui?.obstacleDraw) {
      S.ui.obstacleDraw.currentPoint = { ...end };
      updateObstacleDrawAngleHint(end);
    }
    setStatus('Segment zu kurz');
    requestObstaclePlanRender('obstacle-draw-preview');
    return false;
  }
  recordUndo(`${OBSTACLE_TYPES[type].label} Segment zeichnen`);
  const obstacle = createObstacle(type, start, end, { select: false });
  const drawState = S.ui.obstacleDraw;
  if (obstacle) appendObstacleDrawFixedPoint(end);
  drawState.startPoint = { ...end };
  drawState.currentPoint = { ...end };
  drawState.lengthInput = null;
  clearAngleHint();
  setStatus(obstacle
    ? `${OBSTACLE_TYPES[type].label} erstellt · ${statusSuffix}`
    : 'Hindernis konnte nicht erstellt werden');
  requestObstaclePlanRender('obstacle-draft');
  return Boolean(obstacle);
}

function confirmObstacleLengthInput() {
  const type = obstacleActiveTool();
  const drawState = S.ui?.obstacleDraw;
  const input = drawState?.lengthInput;
  if (!type || !drawState?.startPoint || !input?.active) return false;
  const length = parseObstacleLengthInput(input.value);
  if (!Number.isFinite(length) || length < OBSTACLE_MIN_SEGMENT_LENGTH) {
    setStatus('Bitte eine gültige Länge eingeben');
    requestObstaclePlanRender('obstacle-draw-preview');
    return true;
  }
  const start = { ...drawState.startPoint };
  const end = obstaclePointAtLength(start, input.angle, length);
  commitObstacleSegment(type, start, end);
  return true;
}

function handleGlobalObstacleLengthInputKey(event) {
  if (isObstacleTypingTarget(event.target)) return false;
  const type = obstacleActiveTool();
  const drawState = S.ui?.obstacleDraw;
  if (!type || !drawState?.startPoint) return false;
  const input = drawState.lengthInput;
  if (input?.active) {
    if (event.key === 'Escape') {
      event.preventDefault();
      return cancelObstacleLengthInput();
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      return confirmObstacleLengthInput();
    }
    if (event.key === 'Backspace') {
      event.preventDefault();
      input.value = input.value.slice(0, -1);
      syncObstacleLengthInputPreview();
      AppRuntime.refresh(false);
      return true;
    }
    if (isObstacleLengthInputCharacter(event.key)) {
      event.preventDefault();
      return appendObstacleLengthInput(event.key);
    }
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      return true;
    }
    return false;
  }
  if (isObstacleLengthInputCharacter(event.key)) {
    event.preventDefault();
    return startObstacleLengthInput(event.key);
  }
  return false;
}

function handleGlobalObstacleDrawClick(event) {
  const type = obstacleActiveTool();
  if (!type) return false;
  event.preventDefault();
  event.stopPropagation();
  clearAllObstacleBlockingSelections();
  setObstacleTool(type);

  const drawState = S.ui.obstacleDraw;
  if (drawState.lengthInput?.active) return true;
  const point = obstacleDrawPointFromEvent(event);
  if (!drawState.startPoint) {
    drawState.startPoint = { ...point };
    drawState.currentPoint = { ...point };
    setObstacleDrawFixedStart(point);
    setStatus(`${OBSTACLE_TYPES[type].label} zeichnen · nächsten Punkt setzen`);
    requestObstaclePlanRender('obstacle-draw-click-start');
    return true;
  }

  const start = { ...drawState.startPoint };
  const end = { ...point };
  commitObstacleSegment(type, start, end);
  return true;
}

function updateGlobalObstacleDrawPreview(event) {
  const type = obstacleActiveTool();
  if (!type) return false;
  ensureObstacleState();
  const drawState = S.ui.obstacleDraw;
  if (drawState.startPoint) {
    if (drawState.lengthInput?.active) return true;
    drawState.currentPoint = obstacleDrawPointFromEvent(event);
    updateObstacleDrawAngleHint(drawState.currentPoint);
    requestObstaclePlanRender('obstacle-draw-preview');
  }
  return true;
}

function cancelGlobalObstacleDrawMode(options = {}) {
  const wasActive = Boolean(obstacleActiveTool());
  resetObstacleDrawState();
  if (options.deactivate !== false) setObstacleTool(null);
  if (wasActive) setStatus('Hindernis-Werkzeug deaktiviert');
  requestObstacleAppRefresh('obstacle-tool-cancel');
  return wasActive;
}

/**
 * Beschreibt start Obstacle Draft im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function startObstacleDraft(event) {
  const type = obstacleActiveTool();
  if (!type) return false;
  event.preventDefault();
  const start = obstacleWorldFromMouseEvent(event);
  clearAllObstacleBlockingSelections();
  setObstacleTool(type);
  S.drag = {
    active: true,
    type: 'obstacleDraft',
    obstacleType: type,
    startPoint: { ...start },
    currentPoint: { ...start }
  };
  setStatus(`${OBSTACLE_TYPES[type].label} zeichnen · Endpunkt ziehen`);
  requestObstaclePlanRender('obstacle-draft-start');
  return true;
}

/**
 * Aktualisiert update Obstacle Draft im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Aktualisierung bestehender Daten und abhängiger Berechnungen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function updateObstacleDraft(event) {
  if (S.drag?.type !== 'obstacleDraft') return false;
  S.drag.currentPoint = obstacleWorldFromMouseEvent(event);
  requestObstaclePlanRender('obstacle-draft');
  return true;
}

/**
 * Beschreibt finish Obstacle Draft im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function finishObstacleDraft() {
  if (S.drag?.type !== 'obstacleDraft') return false;
  const type = S.drag.obstacleType;
  const start = S.drag.startPoint;
  const end = S.drag.currentPoint || start;
  const length = Math.hypot(end.x - start.x, end.z - start.z);
  S.drag.active = false;
  S.drag.type = null;
  if (length < OBSTACLE_MIN_DRAW_LENGTH) {
    setStatus('Hindernis zu kurz');
    requestObstaclePlanRender('obstacle-move');
    return true;
  }
  recordUndo(`${OBSTACLE_TYPES[type].label} zeichnen`);
  const obstacle = createObstacle(type, start, end);
  setInteractionFlag('suppressPlanClick', true);
  setStatus(obstacle
    ? `${OBSTACLE_TYPES[type].label} erstellt`
    : 'Hindernis konnte nicht erstellt werden');
  requestObstacleAppRefresh('obstacle-move-drop');
  return true;
}

/**
 * Beschreibt obstacle Distance To Point im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Distance To Point.
 * @param {*} point - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleDistanceToPoint(obstacle, point) {
  const a = obstacle.start;
  const b = obstacle.end;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (len2 <= 0.0001) return Math.hypot(point.x - a.x, point.z - a.z);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / len2));
  const px = a.x + dx * t;
  const pz = a.z + dz * t;
  return Math.hypot(point.x - px, point.z - pz);
}

/**
 * Beschreibt obstacle Bounds im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Bounds.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleBounds(obstacle) {
  const radius = obstaclePlanRadius(obstacle);
  return {
    minX: Math.min(obstacle.start.x, obstacle.end.x) - radius,
    minZ: Math.min(obstacle.start.z, obstacle.end.z) - radius,
    maxX: Math.max(obstacle.start.x, obstacle.end.x) + radius,
    maxZ: Math.max(obstacle.start.z, obstacle.end.z) + radius
  };
}

/**
 * Beschreibt hit Test Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} clientX - Eingabewert für hit Test Obstacle.
 * @param {*} clientY - Eingabewert für hit Test Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function hitTestObstacle(clientX, clientY) {
  ensureObstacleState();
  const svg = getPlanSvg();
  const rect = svg.getBoundingClientRect();
  const world = screenToWorld(clientX - rect.left, clientY - rect.top);
  const screenTolerance = 14 / Math.max(0.05, S.camera2D.scale || 1);
  let best = null;
  for (const obstacle of S.project.obstacles) {
    const distance = obstacleDistanceToPoint(obstacle, world) - obstaclePlanRadius(obstacle);
    const threshold = Math.max(screenTolerance, 6);
    if (distance <= threshold && (!best || distance < best.distance)) {
      best = { kind: 'obstacle', obstacle, distance };
    }
  }
  return best;
}

/**
 * Beschreibt obstacle Center im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Center.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleCenter(obstacle) {
  return {
    x: (obstacle.start.x + obstacle.end.x) / 2,
    z: (obstacle.start.z + obstacle.end.z) / 2
  };
}

/**
 * Beschreibt move Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} dx - Eingabewert für move Obstacle.
 * @param {*} dz - Eingabewert für move Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function moveObstacle(id, dx, dz) {
  const obstacle = obstacleById(id);
  if (!obstacle) return null;
  obstacle.start = { x: obstacle.start.x + dx, z: obstacle.start.z + dz };
  obstacle.end = { x: obstacle.end.x + dx, z: obstacle.end.z + dz };
  return obstacle;
}

/**
 * Beschreibt rotate Obstacle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} angleDeg - Winkel-/Rotationswert zur Ausrichtung im Plan oder Raum.
 * @param {*} pivot - Eingabewert für rotate Obstacle.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function rotateObstacle(id, angleDeg, pivot = null) {
  const obstacle = obstacleById(id);
  if (!obstacle) return null;
  const center = pivot || obstacleCenter(obstacle);
  obstacle.start = obstacleRotatePoint(obstacle.start, center, angleDeg);
  obstacle.end = obstacleRotatePoint(obstacle.end, center, angleDeg);
  return obstacle;
}

/**
 * Aktualisiert set Obstacle Rotation From Base im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} baseObstacle - Eingabewert für set Obstacle Rotation From Base.
 * @param {*} center - Eingabewert für set Obstacle Rotation From Base.
 * @param {*} angleDeg - Winkel-/Rotationswert zur Ausrichtung im Plan oder Raum.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function setObstacleRotationFromBase(id, baseObstacle, center, angleDeg) {
  const obstacle = obstacleById(id);
  if (!obstacle || !baseObstacle) return null;
  obstacle.start = obstacleRotatePoint(baseObstacle.start, center, angleDeg);
  obstacle.end = obstacleRotatePoint(baseObstacle.end, center, angleDeg);
  return obstacle;
}

/**
 * Beschreibt obstacle Rotate Point im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} point - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @param {*} center - Eingabewert für obstacle Rotate Point.
 * @param {*} angleDeg - Winkel-/Rotationswert zur Ausrichtung im Plan oder Raum.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleRotatePoint(point, center, angleDeg) {
  return rotateWorldPoint(point, center, angleDeg);
}

/**
 * Beschreibt start Obstacle Move Drag im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} obstacle - Eingabewert für start Obstacle Move Drag.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function startObstacleMoveDrag(event, obstacle) {
  if (!obstacle) return false;
  event.preventDefault();
  event.stopPropagation();
  const selectedBefore = selectedObstacles();
  const useExistingSelection = selectedBefore.some(entry => entry.id === obstacle.id);
  if (!useExistingSelection) selectObstacle(obstacle.id);
  const selectedIds = (useExistingSelection ? selectedBefore : [obstacle]).map(entry => entry.id);
  const startMouse = obstacleWorldFromMouseEvent(event);
  S.drag = {
    active: true,
    type: 'obstacleMove',
    obstacleId: obstacle.id,
    obstacleIds: selectedIds,
    segmentMoveTopology: createObstacleSegmentMoveTopology(selectedIds),
    pipeMoveTopology: createPipeMoveTopology(selectedIds),
    startMouse,
    lastDelta: { x: 0, z: 0 },
    moved: false,
    undoRecorded: false
  };
  setStatus(`${OBSTACLE_TYPES[obstacle.type].label} wird verschoben`);
  AppRuntime.refresh(false);
  return true;
}

/**
 * Aktualisiert update Obstacle Move Drag im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Aktualisierung bestehender Daten und abhängiger Berechnungen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function updateObstacleMoveDrag(event) {
  if (S.drag?.type !== 'obstacleMove') return false;
  const obstacles = (Array.isArray(S.drag.obstacleIds) && S.drag.obstacleIds.length
    ? S.drag.obstacleIds
    : [S.drag.obstacleId])
    .map(id => obstacleById(id))
    .filter(Boolean);
  if (!obstacles.length) return false;
  const world = obstacleWorldFromMouseEvent(event);
  const dx = world.x - S.drag.startMouse.x;
  const dz = world.z - S.drag.startMouse.z;
  const movedDistance = Math.hypot(dx, dz);
  if (movedDistance > 1 && !S.drag.undoRecorded) {
    recordUndo('Hindernis verschieben');
    S.drag.undoRecorded = true;
  }
  if (S.drag.undoRecorded) {
    const deltaX = dx - (S.drag.lastDelta?.x || 0);
    const deltaZ = dz - (S.drag.lastDelta?.z || 0);
    const segmentIds = new Set(S.drag.segmentMoveTopology?.selectedSegmentIds || S.drag.pipeMoveTopology?.selectedPipeIds || []);
    if (segmentIds.size) {
      updateConnectedObstacleSegmentMove(S.drag.segmentMoveTopology || S.drag.pipeMoveTopology, dx, dz);
    }
    obstacles
      .filter(obstacle => !segmentIds.has(obstacle.id))
      .forEach(obstacle => moveObstacle(obstacle.id, deltaX, deltaZ));
    S.drag.lastDelta = { x: dx, z: dz };
    S.drag.moved = movedDistance > 3;
    requestObstaclePlanRender('obstacle-move');
  }
  return true;
}

/**
 * Beschreibt finish Obstacle Move Drag im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function finishObstacleMoveDrag() {
  if (S.drag?.type !== 'obstacleMove') return false;
  if (S.drag.moved) {
    setInteractionFlag('suppressPlanClick', true);
    setStatus('Hindernis verschoben');
  } else {
    setStatus('Hindernis ausgewählt');
  }
  S.drag.active = false;
  S.drag.type = null;
  requestObstacleAppRefresh('obstacle-move-drop', S.drag.undoRecorded === true);
  return true;
}

/**
 * Beschreibt start Obstacle Rotate Press im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @param {*} obstacleId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function startObstacleRotatePress(event, obstacleId) {
  const obstacle = obstacleById(obstacleId);
  if (!obstacle) return false;
  event.preventDefault();
  event.stopPropagation();
  selectObstacle(obstacle.id);
  clearInteractionTimer('obstacleRotateHold');
  const world = obstacleWorldFromMouseEvent(event);
  const center = obstacleCenter(obstacle);
  obstacleRotatePress = {
    obstacleId: obstacle.id,
    clientX: event.clientX,
    clientY: event.clientY,
    freeRotateStarted: false
  };
  setInteractionTimer('obstacleRotateHold', setTimeout(() => {
    const press = obstacleRotatePress;
    if (!press || press.obstacleId !== obstacle.id) return;
    const current = obstacleById(obstacle.id);
    if (!current) return;
    press.freeRotateStarted = true;
    recordUndo('Hindernis frei drehen');
    S.drag = {
      active: true,
      type: 'obstacleRotate',
      obstacleId: obstacle.id,
      center,
      startPointerAngle: obstaclePointerAngle(center, world),
      startObstacle: {
        start: { ...current.start },
        end: { ...current.end }
      }
    };
    setInteractionFlag('suppressObstacleRotateClick', true);
    setStatus(`${OBSTACLE_TYPES[current.type].label} wird frei gedreht`);
    requestObstaclePlanRender('obstacle-rotate-start');
  }, 280));
  return true;
}

/**
 * Aktualisiert update Obstacle Rotate Drag im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} event - Browser-/Pointer-Event aus der Bedienoberfläche.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function updateObstacleRotateDrag(event) {
  if (S.drag?.type !== 'obstacleRotate') return false;
  const obstacle = obstacleById(S.drag.obstacleId);
  if (!obstacle) return false;
  const world = obstacleWorldFromMouseEvent(event);
  const angle = obstaclePointerAngle(S.drag.center, world) - S.drag.startPointerAngle;
  const previousStart = obstacle.start;
  const previousEnd = obstacle.end;
  setObstacleRotationFromBase(obstacle.id, S.drag.startObstacle, S.drag.center, angle);
  if (obstacle.start.x !== previousStart.x || obstacle.start.z !== previousStart.z
    || obstacle.end.x !== previousEnd.x || obstacle.end.z !== previousEnd.z) {
    S.drag.geometryChanged = true;
  }
  setStatus(`${OBSTACLE_TYPES[obstacle.type].label} wird frei gedreht · ${Math.round(angle)}°`);
  requestObstaclePlanRender('obstacle-rotate');
  return true;
}

/**
 * Beschreibt finish Obstacle Rotate Drag im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function finishObstacleRotateDrag() {
  if (S.drag?.type !== 'obstacleRotate') return false;
  obstacleRotatePress = null;
  setInteractionFlag('suppressPlanClick', true);
  setStatus('Hindernis gedreht');
  S.drag.active = false;
  S.drag.type = null;
  requestObstacleAppRefresh('obstacle-rotate-drop', S.drag.geometryChanged === true);
  return true;
}

/**
 * Beschreibt obstacle Pointer Angle im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Kollisionen und Hindernisse.
 * - Kapselt Winkel-, Dreh- oder Befestigungslogik, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} center - Eingabewert für obstacle Pointer Angle.
 * @param {*} point - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstaclePointerAngle(center, point) {
  return Math.atan2(point.z - center.z, point.x - center.x) * 180 / Math.PI;
}

export {
  OBSTACLE_TYPES,
  OBSTACLE_MIN_DRAW_LENGTH,
  OBSTACLE_MIN_SEGMENT_LENGTH,
  requestObstaclePlanRender,
  requestObstacleAppRefresh,
  ensureObstacleState,
  normalizeObstaclePoint,
  normalizeObstacleDrawPoint,
  obstaclePointsEqual,
  normalizeObstacleDrawState,
  normalizeObstacle,
  defaultObstacleConfig,
  pendingObstacleConfig,
  setPendingObstacleConfig,
  obstacleConfigFromObstacle,
  obstacleActiveTool,
  isGlobalObstacleDrawToolActive,
  resetObstacleDrawState,
  setObstacleTool,
  activateObstacleTool,
  syncObstacleToolbarButtons,
  clearAllObstacleBlockingSelections,
  clearObstacleSelection,
  obstacleById,
  selectedObstacle,
  selectedObstacles,
  selectObstacle,
  createObstacle,
  updateObstacle,
  deleteObstacle,
  deleteSelectedObstacles,
  obstacleHeightRange,
  obstaclePlanRadius,
  buildObstacleSegmentConnectionGraph,
  buildPipeConnectionGraph,
  detectObstacleSegmentConnections,
  detectPipeConnections,
  buildObstacleSegmentRenderSegments,
  buildPipeRenderSegments,
  buildRectangularObstacleRenderSegments,
  attachRectangularObstacleMiterPolygons,
  rectangularObstacleMiterLookup,
  rectangularObstacleEndpointMiterInfo,
  rectangularObstacleMiterPolygons,
  rectangularObstacleMiterOffsetPolygon,
  rectangularObstacleMiterOffsetPoint,
  rectangularObstacleLineIntersection,
  obstacleSegmentJointCanMiter,
  createObstacleSegmentMoveTopology,
  createPipeMoveTopology,
  updateConnectedObstacleSegmentMove,
  updateConnectedPipeMove,
  pipeGraphModel,
  obstacleSegmentGraphModel,
  obstacleSegmentGraphRadius,
  obstacleSegmentGraphRenderKey,
  pipeGraphAddPairJoints,
  pipeGraphPushEndpointProjectionJoint,
  pipeGraphSegmentIntersection,
  pipeGraphLineIntersection,
  pipeGraphConstrainedJointPoint,
  pipeGraphCollinearOverlap,
  pipeGraphPushJoint,
  pipeGraphMergeJoints,
  pipeGraphEndpointEntries,
  pipeGraphConnectionAtT,
  pipeGraphConnectionAtPoint,
  pipeGraphConnectionPoint,
  pipeGraphApplyConnectionPoint,
  pipeGraphProjectPointToSegment,
  pipeGraphProjectPointToLine,
  pipeGraphPointLineDistance,
  pipeGraphDimensionsCompatible,
  pipeGraphModelFitsRenderGroup,
  pipeGraphLineOffset,
  pipeGraphCloneObstacle,
  pipeGraphCross,
  pipeGraphDot,
  obstacleWorldFromMouseEvent,
  obstacleConstrainPoint,
  obstacleDrawPointFromEvent,
  obstacleDrawAngle,
  obstaclePointAtLength,
  calculateSegmentAngle,
  formatSegmentAngle,
  clearAngleHint,
  obstacleDrawFixedPoints,
  setObstacleDrawFixedStart,
  appendObstacleDrawFixedPoint,
  updateObstacleDrawAngleHint,
  parseObstacleLengthInput,
  syncObstacleLengthInputPreview,
  isObstacleLengthInputCharacter,
  startObstacleLengthInput,
  appendObstacleLengthInput,
  cancelObstacleLengthInput,
  commitObstacleSegment,
  confirmObstacleLengthInput,
  handleGlobalObstacleLengthInputKey,
  handleGlobalObstacleDrawClick,
  updateGlobalObstacleDrawPreview,
  cancelGlobalObstacleDrawMode,
  startObstacleDraft,
  updateObstacleDraft,
  finishObstacleDraft,
  obstacleDistanceToPoint,
  obstacleBounds,
  hitTestObstacle,
  obstacleCenter,
  moveObstacle,
  rotateObstacle,
  setObstacleRotationFromBase,
  obstacleRotatePoint,
  startObstacleMoveDrag,
  updateObstacleMoveDrag,
  finishObstacleMoveDrag,
  startObstacleRotatePress,
  updateObstacleRotateDrag,
  finishObstacleRotateDrag,
  obstaclePointerAngle
};
